import React from 'react';
import type { FieldConfig } from 'formik';
import { useField, useFormikContext } from 'formik';
import styled, { css } from 'styled-components';
import InputMask from 'react-input-mask';

type CustomerNameInput = {
  name: string;
  className?: string;
  header: string;
  placeholder: string;
  isMobile: boolean;
  disabled?: boolean;
} & FieldConfig;

const CustomerPassportNumberInput = (props: CustomerNameInput) => {
  const {
    name,
    className,
    header,
    placeholder,
    isMobile,
    disabled,
    ...formikRest
  } = props;

  const [field] = useField({ name });
  const [docTypeField] = useField('docType');
  const { setFieldValue, errors, touched } = useFormikContext();

  const isValid = Boolean(!errors[name] && touched[name]);
  const isInvalid = Boolean(errors[name] && touched[name]);

  const changeHandler: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setFieldValue(name, e.target.value);
  };

  const fieldProps = {
    ...field,
    ...formikRest,
    placeholder: disabled ? '' : placeholder,
    onChange: changeHandler,
    mask:
      docTypeField.value === 'internal_passport' ? '9999 999999' : undefined,
  };

  return (
    <InputWrapper className={className}>
      <InputHeader $isMobile={isMobile}>{header}</InputHeader>
      <Input
        disabled={disabled}
        $isMobile={isMobile}
        $valid={isValid}
        $invalid={isInvalid}
        {...fieldProps}
      />
      {isInvalid && (
        <ValidationError $isMobile={isMobile}>{errors[name]}</ValidationError>
      )}
    </InputWrapper>
  );
};

const InputWrapper = styled.div``;

const ValidationError = styled.div<{ $isMobile: boolean }>`
  max-width: 200px;
  margin-top: 4px;
  color: #d0021b;
  padding-right: 4px;

  ${(props) =>
    props.$isMobile &&
    css`
      font-size: 11px;
      padding-right: 0;
    `}
`;

const InputHeader = styled.div<{ $isMobile: boolean }>`
  color: #4d4d4d;

  font-size: 12px;
  line-height: 16px;
  margin-bottom: 4px;
  padding-left: 2px;

  ${(props) =>
    props.$isMobile &&
    css`
      font-size: 9px;
      line-height: 12px;
      letter-spacing: 0.9px;
      text-transform: uppercase;
      padding-left: 0px;
    `}
`;

export const Input = styled(InputMask)<{
  $isMobile: boolean;
  $valid: boolean;
  $invalid: boolean;
}>`
  width: 100%;
  height: 48px;
  padding: 16px 8px;

  border: 1px solid #c2c5cc;
  background: #fff;

  color: #17181a;
  font-size: 14px;
  font-weight: 700;

  &:placeholder-shown {
    color: #919499;
    font-weight: 400;
  }

  ${(props) =>
    props.$valid &&
    css`
      background: #f1fbe8;
    `}

  ${(props) =>
    props.$invalid &&
    css`
      border: 1px solid #d0021b;
      background-color: #f9eaec;
    `}

  ${(props) =>
    props.disabled &&
    css`
      color: transparent;
      background: transparent;
      border: 1px solid #c2c5cc;
    `}

  ${(props) =>
    props.$isMobile &&
    css`
      font-size: 18px;
      font-weight: bold;
    `}
`;

export default CustomerPassportNumberInput;
